import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, Redirect, useParams } from "react-router-dom";
import {
  FaCheck,
  FaEdit,
  FaFileAlt,
  FaLayerGroup,
  FaPlus,
  FaSyncAlt,
  FaTimes,
  FaTrash,
  FaUsers,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import BackButton from "../ChildComponent/BackButton";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  '"Segoe UI", system-ui, -apple-system, Roboto, "Helvetica Neue", Arial, sans-serif';

const SCALE_MIN = 0;
const SCALE_MAX = 100;
const EPS = 0.0001;

function classChipLabel(item) {
  if (!item) return "—";
  const sectionId = item.section_id;
  if (sectionId === "No" || sectionId === "null" || !sectionId) {
    return item.class_name || "—";
  }
  return `${item.class_name || "—"}:${item.section_name || ""}`;
}

function resultTone(result) {
  const r = String(result || "")
    .trim()
    .toLowerCase();
  if (!r) return "";
  if (r.includes("pass") || r.includes("promot") || r === "p") return "pass";
  if (r.includes("fail") || r.includes("detain") || r === "f") return "fail";
  return "neutral";
}

function normalizeBand(row = {}) {
  return {
    from_value: row.from_value ?? "",
    to_value: row.to_value ?? "",
    marking: row.marking ?? "",
    performance_descriptors: row.performance_descriptors ?? "",
    result: row.result ?? "",
    remark: row.remark ?? "",
    grade_point: row.grade_point ?? 0,
  };
}

function parseNum(value) {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Validates bands for report-card matching: contiguous [from,to) … last [from,to], cover 0–100. */
function validateMarkingBands(rows) {
  const errors = [];
  const rowErrors = rows.map(() => ({}));

  if (!rows.length) {
    errors.push("Add at least one marking band.");
    return { ok: false, errors, rowErrors };
  }

  const parsed = rows.map((row, index) => {
    const from = parseNum(row.from_value);
    const to = parseNum(row.to_value);
    const marking = String(row.marking || "").trim();
    const performance = String(row.performance_descriptors || "").trim();
    const result = String(row.result || "").trim();
    const local = {};

    if (from === null) local.from_value = "Enter a number";
    if (to === null) local.to_value = "Enter a number";
    if (from !== null && to !== null && from >= to) {
      local.to_value = "Must be greater than From";
    }
    if (!marking) local.marking = "Required";
    else if (marking.length > 30) local.marking = "Max 30 characters";
    if (performance.length > 30) {
      local.performance_descriptors = "Max 30 characters";
    }
    if (result.length > 50) local.result = "Max 50 characters";

    rowErrors[index] = local;
    return { from, to };
  });

  if (parsed[0].from !== null && Math.abs(parsed[0].from - SCALE_MIN) > EPS) {
    rowErrors[0].from_value = `Must start at ${SCALE_MIN}`;
    errors.push(`First band From must be ${SCALE_MIN}.`);
  }

  const last = parsed[parsed.length - 1];
  if (last.to !== null && Math.abs(last.to - SCALE_MAX) > EPS) {
    rowErrors[parsed.length - 1].to_value = `Must end at ${SCALE_MAX}`;
    errors.push(`Last band To must be ${SCALE_MAX}.`);
  }

  for (let i = 1; i < parsed.length; i++) {
    const prev = parsed[i - 1];
    const curr = parsed[i];
    if (prev.to === null || curr.from === null) continue;
    if (Math.abs(curr.from - prev.to) > EPS) {
      const msg =
        curr.from > prev.to
          ? `Gap after band ${i}: next From must equal ${prev.to}`
          : `Overlap with band ${i}: next From must equal ${prev.to}`;
      rowErrors[i].from_value = `Must equal previous To (${prev.to})`;
      errors.push(msg);
    }
  }

  rows.forEach((_, index) => {
    if (Object.keys(rowErrors[index]).length) {
      const text = `Band ${index + 1} has invalid values.`;
      if (!errors.includes(text)) errors.push(text);
    }
  });

  const ok = errors.length === 0;
  return { ok, errors, rowErrors };
}

function emptyBand(from = "", to = "") {
  return normalizeBand({
    from_value: from,
    to_value: to,
    marking: "",
    performance_descriptors: "",
    result: "",
    remark: "",
    grade_point: 0,
  });
}

//Created By Cursor
export default function ExamMarkingSystemAnnual() {
  const { report_card_id: reportCardId, session_id: sessionId } = useParams();

  const [loggedIn] = useState(() => !!localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [examData, setExamData] = useState(null);
  const [participatingClasses, setParticipatingClasses] = useState([]);
  const [selectedClassIndex, setSelectedClassIndex] = useState(0);
  const [markingValues, setMarkingValues] = useState([]);
  const [examId, setExamId] = useState("");
  const [editing, setEditing] = useState(false);
  const [draftRows, setDraftRows] = useState([]);

  const applyClassMarking = useCallback((classes, index) => {
    if (index < 0 || index >= classes.length) {
      setMarkingValues([]);
      return;
    }
    const ms = classes[index]?.marking_system;
    setMarkingValues(
      Array.isArray(ms?.[0]?.MarkingSystemValue)
        ? ms[0].MarkingSystemValue.map(normalizeBand)
        : []
    );
  }, []);

  const loadMarkingSystem = useCallback(
    async ({ soft = false, keepClassIndex = null } = {}) => {
      if (!reportCardId || !sessionId) {
        setExamData(null);
        setParticipatingClasses([]);
        setMarkingValues([]);
        setExamId("");
        setLoading(false);
        return;
      }

      if (soft) setRefreshing(true);
      else setLoading(true);

      try {
        const schoolCode = SessionManager.getSchoolCode();
        const response = await axios
          .get(`${API_BASE()}getExamMarkingSystem.php`, {
            params: {
              school_code: schoolCode,
              session_id: sessionId,
              exam_id: "",
              report_card_type: "ANNUAL",
              report_card_id: reportCardId,
            },
          })
          .then((res) => res.data);

        if (response?.api_status === "OK") {
          const rows = Array.isArray(response.data) ? response.data : [];
          const first = rows[0] || null;
          const classes = first?.participating_class || [];
          const nextExamId = first?.concerned_exams || "";

          setExamData(first);
          setExamId(nextExamId);
          setParticipatingClasses(classes);

          let nextIndex = classes.length ? 0 : -1;
          if (
            keepClassIndex != null &&
            keepClassIndex >= 0 &&
            keepClassIndex < classes.length
          ) {
            nextIndex = keepClassIndex;
          }
          setSelectedClassIndex(nextIndex);
          applyClassMarking(classes, nextIndex);
        } else {
          setExamData(null);
          setParticipatingClasses([]);
          setMarkingValues([]);
          setExamId("");
          myToast.notify_warning(
            response?.api_remark || "Failed to load marking system."
          );
        }
      } catch (error) {
        console.log("error>>>>>", error);
        setExamData(null);
        setParticipatingClasses([]);
        setMarkingValues([]);
        setExamId("");
        myToast.notify_warning("Failed to load marking system.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [reportCardId, sessionId, applyClassMarking]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    loadMarkingSystem();
  }, [loadMarkingSystem]);

  const selectClass = useCallback(
    (index) => {
      if (editing) {
        myToast.notify_warning("Finish or cancel editing before switching class.");
        return;
      }
      if (index < 0 || index >= participatingClasses.length) return;
      setSelectedClassIndex(index);
      applyClassMarking(participatingClasses, index);
    },
    [editing, participatingClasses, applyClassMarking]
  );

  const selectedClass = participatingClasses[selectedClassIndex] || null;
  const markingMeta = selectedClass?.marking_system?.[0] || null;
  const markingSystemId = markingMeta?.marking_system_id || "";
  const markingValueId = markingMeta?.marking_value_id || "";

  const validation = useMemo(
    () => (editing ? validateMarkingBands(draftRows) : { ok: true, errors: [], rowErrors: [] }),
    [editing, draftRows]
  );

  const startEdit = useCallback(() => {
    if (!markingValues.length) {
      setDraftRows([emptyBand(SCALE_MIN, SCALE_MAX)]);
    } else {
      setDraftRows(markingValues.map(normalizeBand));
    }
    setEditing(true);
  }, [markingValues]);

  const cancelEdit = useCallback(() => {
    if (saving) return;
    setEditing(false);
    setDraftRows([]);
  }, [saving]);

  const updateDraftField = useCallback((index, field, value) => {
    setDraftRows((prev) => {
      const next = prev.map((row, i) =>
        i === index ? { ...row, [field]: value } : row
      );

      // Keep bands contiguous: changing To chains the next From (and vice versa).
      if (field === "to_value" && index < next.length - 1) {
        next[index + 1] = { ...next[index + 1], from_value: value };
      }
      if (field === "from_value" && index > 0) {
        next[index - 1] = { ...next[index - 1], to_value: value };
      }
      return next;
    });
  }, []);

  const addBand = useCallback(() => {
    setDraftRows((prev) => {
      if (!prev.length) return [emptyBand(SCALE_MIN, SCALE_MAX)];
      const last = prev[prev.length - 1];
      const splitAt = parseNum(last.to_value);
      const from = parseNum(last.from_value);
      if (from === null || splitAt === null || splitAt - from <= 1) {
        myToast.notify_warning(
          "Widen the last band before splitting it into a new row."
        );
        return prev;
      }
      const mid = Math.round((from + splitAt) / 2);
      const updated = [...prev];
      updated[updated.length - 1] = {
        ...last,
        to_value: mid,
      };
      updated.push(
        emptyBand(mid, splitAt === SCALE_MAX ? SCALE_MAX : splitAt)
      );
      // Ensure last still ends at previous last to
      updated[updated.length - 1].to_value = last.to_value;
      return updated;
    });
  }, []);

  const removeBand = useCallback((index) => {
    setDraftRows((prev) => {
      if (prev.length <= 1) {
        myToast.notify_warning("At least one band is required.");
        return prev;
      }
      const next = prev.filter((_, i) => i !== index);
      // Re-stitch boundaries so coverage stays contiguous.
      for (let i = 1; i < next.length; i++) {
        next[i] = { ...next[i], from_value: next[i - 1].to_value };
      }
      if (next.length) {
        next[0] = { ...next[0], from_value: SCALE_MIN };
        next[next.length - 1] = {
          ...next[next.length - 1],
          to_value: SCALE_MAX,
        };
      }
      return next;
    });
  }, []);

  const saveMarkingSystem = useCallback(async () => {
    const check = validateMarkingBands(draftRows);
    if (!check.ok) {
      myToast.notify_warning(check.errors[0] || "Fix boundary errors before saving.");
      return;
    }
    if (!markingSystemId || !markingValueId) {
      myToast.notify_warning("Missing marking system identifiers.");
      return;
    }

    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      myToast.notify_warning("Missing school code.");
      return;
    }

    const payload = draftRows.map((row) => ({
      from_value: Number(row.from_value),
      to_value: Number(row.to_value),
      marking: String(row.marking || "").trim(),
      performance_descriptors: String(row.performance_descriptors || "").trim(),
      result: String(row.result || "").trim(),
      remark: String(row.remark || "").trim(),
      grade_point: Number(row.grade_point) || 0,
    }));

    setSaving(true);
    try {
      const response = await axios
        .post(`${API_BASE()}updateExamMarkingSystemValues.php`, null, {
          params: {
            school_code: schoolCode,
            marking_system_id: markingSystemId,
            marking_value_id: markingValueId,
            values: JSON.stringify(payload),
          },
        })
        .then((res) => res.data);

      if (response?.api_status === "OK") {
        myToast.notify_success(
          response.api_remark || "Marking system updated successfully."
        );
        setEditing(false);
        setDraftRows([]);
        await loadMarkingSystem({
          soft: true,
          keepClassIndex: selectedClassIndex,
        });
      } else {
        myToast.notify_warning(
          response?.api_remark || "Failed to update marking system."
        );
      }
    } catch (error) {
      console.log("error>>>>>", error);
      myToast.notify_warning("Error updating marking system.");
    } finally {
      setSaving(false);
    }
  }, [
    draftRows,
    markingSystemId,
    markingValueId,
    loadMarkingSystem,
    selectedClassIndex,
  ]);

  const meta = useMemo(
    () => ({
      sessionName: examData?.session_name || "—",
      caption: examData?.report_card_name || "—",
    }),
    [examData]
  );

  const reportCardTo =
    reportCardId && examId && sessionId
      ? `/AdminMain/ExamStudentReportCard/${reportCardId}/${encodeURIComponent(
          String(examId)
        )}/${sessionId}/null`
      : null;

  const displayRows = editing ? draftRows : markingValues;
  const busy = loading || refreshing || saving;

  if (!loggedIn) return <Redirect to="/Login" />;
              
         return (
    <div
      className="content payroll-page sm-page emsa-page"
      style={{ background: COLORS.white, fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
            <div className="leftside-content-header">
              <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}>
                {" "}
                Exam Performance Descriptor
              </span>
            </li>
              </ul>
            </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
          </div>    
        
      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell emsa-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaLayerGroup />
                </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Performance Descriptor</h1>
              <p className="sm-hero__sub">
                {examData
                  ? `${meta.caption} · ${meta.sessionName}`
                  : "Annual report card grade bands and descriptors"}
              </p>
                </div>
          </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              onClick={() =>
                loadMarkingSystem({
                  soft: true,
                  keepClassIndex: selectedClassIndex,
                })
              }
              disabled={busy || editing}
              aria-label="Refresh"
              title="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
            {reportCardTo ? (
              <Link to={reportCardTo} className="emsa-cta">
                <FaFileAlt aria-hidden />
                <span>Get Report Card</span>
                     </Link>
            ) : (
              <button type="button" className="emsa-cta" disabled>
                <FaFileAlt aria-hidden />
                <span>Get Report Card</span>
              </button>
            )}
                    </div>
        </header>

        <div className="emsa-body">
          {loading ? (
            <div className="emsa-loading">
              <MyLoader />
               </div>
          ) : !examData ? (
            <div className="emsa-empty">
              <div className="emsa-empty__icon" aria-hidden>
                <FaLayerGroup />
                    </div>
              <p className="emsa-empty__title">No performance descriptor found</p>
              <p className="emsa-empty__hint">
                Grade bands for this report card could not be loaded.
              </p>
              <button
                type="button"
                className="emsa-btn"
                onClick={() => loadMarkingSystem()}
              >
                Try again
              </button>
               </div>
          ) : (
            <>
              <section className="emsa-panel" aria-label="Participating classes">
                <div className="emsa-panel__head">
                  <h2>
                    <FaUsers aria-hidden /> Participating classes
                  </h2>
                  <span>
                    {participatingClasses.length} class
                    {participatingClasses.length === 1 ? "" : "es"}
                  </span>
         </div>
                {participatingClasses.length === 0 ? (
                  <div className="emsa-empty emsa-empty--soft">
                    <p className="emsa-empty__title">No classes linked</p>
                    <p className="emsa-empty__hint">
                      This report card has no participating classes.
                    </p>
                        </div>
                ) : (
                  <div className="emsa-chips" role="list">
                    {participatingClasses.map((item, index) => {
                      const active = selectedClassIndex === index;
                      return (
                        <button
                          key={`${item.class_id}-${item.section_id}-${index}`}
                          type="button"
                          role="listitem"
                          className={`emsa-chip${active ? " is-active" : ""}`}
                          onClick={() => selectClass(index)}
                          aria-pressed={active}
                          disabled={editing && !active}
                        >
                          {classChipLabel(item)}
                        </button>
                      );
                    })}
                    </div>
                )}
              </section>

              <section className="emsa-panel" aria-label="Performance descriptor table">
                <div className="emsa-panel__head">
                  <h2>
                    <FaLayerGroup aria-hidden /> Performance descriptor
                  </h2>
                  <div className="emsa-panel__head-actions">
                    <span>
                      {selectedClass
                        ? classChipLabel(selectedClass)
                        : "Select a class"}
                    </span>
                    {!editing ? (
                      <button
                        type="button"
                        className="emsa-head-btn"
                        onClick={startEdit}
                        disabled={busy || !selectedClass}
                        title="Edit performance descriptor"
                      >
                        <FaEdit aria-hidden /> Edit
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="emsa-head-btn emsa-head-btn--ghost"
                          onClick={cancelEdit}
                          disabled={saving}
                        >
                          <FaTimes aria-hidden /> Cancel
                        </button>
                        <button
                          type="button"
                          className="emsa-head-btn"
                          onClick={saveMarkingSystem}
                          disabled={saving || !validation.ok}
                          title={
                            validation.ok
                              ? "Save performance descriptor"
                              : "Fix boundary errors first"
                          }
                        >
                          <FaCheck aria-hidden />{" "}
                          {saving ? "Saving…" : "Save"}
                        </button>
                      </>
                    )}
                      </div>
                    </div>
                  
                {editing && (
                  <div
                    className={`emsa-validate${
                      validation.ok ? " is-ok" : " is-bad"
                    }`}
                    role="status"
                  >
                    {validation.ok ? (
                      <p>
                        Boundaries look correct — From is included, To is
                        excluded, except {SCALE_MAX} which is included. Continuous
                        coverage from {SCALE_MIN} to {SCALE_MAX} with no gaps or
                        overlaps.
                      </p>
                    ) : (
                      <ul>
                        {validation.errors.slice(0, 4).map((err) => (
                          <li key={err}>{err}</li>
                        ))}
                      </ul>
                    )}
                    <p className="emsa-validate__hint">
                      Rule: From (included), To (excluded), except {SCALE_MAX}.
                      Example: 0–33 covers 0 up to but not 33; 90–100 covers 90
                      through 100. Adjacent bands must touch: next From = previous
                      To. Shared for all classes using this marking set.
                    </p>
                  </div>
                )}

                {displayRows.length === 0 ? (
                  <div className="emsa-empty emsa-empty--soft">
                    <p className="emsa-empty__title">No grade bands</p>
                    <p className="emsa-empty__hint">
                      Select a class to view its performance descriptor values.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="emsa-table-wrap">
                      <table className={`emsa-table${editing ? " is-editing" : ""}`}>
                          <thead>
                            <tr>
                            <th>Sr.</th>
                            <th>From (Included)</th>
                            <th>To (Excluded) Except {SCALE_MAX}</th>
                            <th>Marking</th>
                            <th>Performance descriptor</th>
                            <th>Result</th>
                            {editing && <th> </th>}
                            </tr>
                          </thead>
                          <tbody>
                          {displayRows.map((row, index) => {
                            const tone = resultTone(row.result);
                            const fieldErr = validation.rowErrors[index] || {};
                            if (!editing) {
                              return (
                                <tr
                                  key={`${row.marking}-${row.from_value}-${index}`}
                                >
                                  <td>{index + 1}</td>
                                  <td>{row.from_value}</td>
                                  <td>{row.to_value}</td>
                                  <td>
                                    <span className="emsa-mark">
                                      {row.marking}
                                    </span>
                                  </td>
                                  <td className="emsa-table__desc">
                                    {row.performance_descriptors || "—"}
                                  </td>
                                  <td>
                                    {row.result ? (
                                      <span
                                        className={`emsa-result${
                                          tone ? ` emsa-result--${tone}` : ""
                                        }`}
                                      >
                                        {row.result}
                                      </span>
                                    ) : (
                                      "—"
                                    )}
                                  </td>
                                </tr>
                              );
                            }

                            return (
                              <tr key={`draft-${index}`}>
                                <td>{index + 1}</td>
                                <td>
                                  <input
                                    type="number"
                                    className={`emsa-input${
                                      fieldErr.from_value ? " is-invalid" : ""
                                    }`}
                                    value={row.from_value}
                                    onChange={(e) =>
                                      updateDraftField(
                                        index,
                                        "from_value",
                                        e.target.value
                                      )
                                    }
                                    disabled={saving}
                                    title={fieldErr.from_value || ""}
                                    step="any"
                                  />
                                  {fieldErr.from_value && (
                                    <span className="emsa-field-err">
                                      {fieldErr.from_value}
                                    </span>
                                  )}
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    className={`emsa-input${
                                      fieldErr.to_value ? " is-invalid" : ""
                                    }`}
                                    value={row.to_value}
                                    onChange={(e) =>
                                      updateDraftField(
                                        index,
                                        "to_value",
                                        e.target.value
                                      )
                                    }
                                    disabled={saving}
                                    title={fieldErr.to_value || ""}
                                    step="any"
                                  />
                                  {fieldErr.to_value && (
                                    <span className="emsa-field-err">
                                      {fieldErr.to_value}
                                    </span>
                                  )}
                                </td>
                                <td>
                                  <input
                                    type="text"
                                    className={`emsa-input emsa-input--mark${
                                      fieldErr.marking ? " is-invalid" : ""
                                    }`}
                                    value={row.marking}
                                    maxLength={30}
                                    onChange={(e) =>
                                      updateDraftField(
                                        index,
                                        "marking",
                                        e.target.value
                                      )
                                    }
                                    disabled={saving}
                                  />
                                  {fieldErr.marking && (
                                    <span className="emsa-field-err">
                                      {fieldErr.marking}
                                    </span>
                                  )}
                                </td>
                                <td>
                                  <input
                                    type="text"
                                    className={`emsa-input emsa-input--wide${
                                      fieldErr.performance_descriptors
                                        ? " is-invalid"
                                        : ""
                                    }`}
                                    value={row.performance_descriptors}
                                    maxLength={30}
                                    onChange={(e) =>
                                      updateDraftField(
                                        index,
                                        "performance_descriptors",
                                        e.target.value
                                      )
                                    }
                                    disabled={saving}
                                  />
                                  {fieldErr.performance_descriptors && (
                                    <span className="emsa-field-err">
                                      {fieldErr.performance_descriptors}
                                    </span>
                                  )}
                                </td>
                                <td>
                                  <input
                                    type="text"
                                    className={`emsa-input${
                                      fieldErr.result ? " is-invalid" : ""
                                    }`}
                                    value={row.result}
                                    maxLength={50}
                                    onChange={(e) =>
                                      updateDraftField(
                                        index,
                                        "result",
                                        e.target.value
                                      )
                                    }
                                    disabled={saving}
                                    placeholder="Pass / Fail"
                                  />
                                  {fieldErr.result && (
                                    <span className="emsa-field-err">
                                      {fieldErr.result}
                                    </span>
                                  )}
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="emsa-icon-del"
                                    onClick={() => removeBand(index)}
                                    disabled={saving || displayRows.length <= 1}
                                    title="Remove band"
                                    aria-label="Remove band"
                                  >
                                    <FaTrash />
                                  </button>
                                </td>
                            </tr>
                            );
                        })}
                          </tbody>
                      </table>
                  </div>
                                 
                    {editing && (
                      <div className="emsa-edit-footer">
                        <button
                          type="button"
                          className="emsa-btn emsa-btn--ghost"
                          onClick={addBand}
                          disabled={saving}
                        >
                          <FaPlus aria-hidden /> Add band
                        </button>
                  </div>
                    )}
                  </>
                )}
              </section>
            </>
          )}
                  </div>
                </div> 
                </div>  
  );
}
